import React, { useState, useEffect, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Modal, Button, Table, Form, Offcanvas } from "react-bootstrap";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import {
  resumeSims,
  validateSimsForAction,
} from "../../../../redux/slices/sims";
import * as Yup from "yup";
import { Formik } from "formik";
import Loader from "../../../../components/spinners/Loader";
import NotyfContext from "../../../../contexts/NotyfContext";
import useWindowSize from "../../../../hooks/useWindowSize";
import useDeviceType from "../../../../hooks/useDeviceType";

const ResumeSimModal = ({
  sims,
  openResumeModal,
  setOpenResumeModal,
  clearSelectedRows,
}) => {
  const whitelabel = useSelector((state) => state.whitelabel.whitelabel);
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(1);
  const notyf = useContext(NotyfContext);
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const [internalShow, setInternalShow] = useState(openResumeModal);
  const [fadeOut, setFadeOut] = useState(false);
  const [fileInputKey, setFileInputKey] = React.useState(Date.now());

  useEffect(() => {
    if (openResumeModal && !isDesktop) {
      setFadeOut(false);
      setInternalShow(true);
    }
  }, [openResumeModal, isDesktop]);

  const handleHide = () => {
    setFadeOut(true);
    setTimeout(() => {
      setInternalShow(false);
      setOpenResumeModal(false);
    }, 300);
  };

  const initialValues = {
    sims: sims.length > 0 ? sims : [],
    file: null,
  };

  const validation = Yup.object().shape({
    sims: Yup.array().required("Please select at least one SIM"),
    file: Yup.mixed().when("$requireFile", {
      is: true,
      then: Yup.mixed()
        .required("File is required")
        .test("fileType", "Only CSV or XLSX files are accepted", (value) => {
          if (!value) return false;
          const allowedTypes = [
            "text/csv",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.ms-excel",
          ];
          return allowedTypes.includes(value.type);
        }),
      otherwise: Yup.mixed().nullable().notRequired(),
    }),
  });

  if (isDesktop) {
    return (
      <Modal
        show={openResumeModal}
        onHide={() => setOpenResumeModal(false)}
        centered
        size="lg"
        backdrop="static"
        scrollable
      >
        <Modal.Header>Resume SIMs</Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={initialValues}
            validationSchema={validation}
            context={{ requireFile: sims.length === 0 }}
            onSubmit={async (values, { setValues, setStatus }) => {
              const data = {
                sims: values.sims.map((sim) => sim.imsi),
              };
              await dispatch(resumeSims(data.sims))
                .then(() => {
                  notyf.open({
                    type: "success",
                    message: "SIMs resumed successfully",
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                  setStatus("Success");
                })
                .catch((error) => {
                  notyf.open({
                    type: "danger",
                    message: error,
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                  setStatus("Error");
                })
                .finally(() => {
                  setCurrentStep(3);
                  setValues(initialValues);
                });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              status,
              validateForm,
              setFieldTouched,
              setFieldValue,
            }) => {
              const nextStep = () => {
                validateForm().then((errors) => {
                  if (Object.keys(errors).length === 0) {
                    if (currentStep === 1) {
                      setCurrentStep(2);
                      handleSubmit();
                    }
                  } else {
                    Object.keys(values).forEach((field) => {
                      setFieldTouched(field);
                    });
                  }
                });
              };

              return (
                <Form onSubmit={handleSubmit}>
                  {currentStep === 1 && values.sims.length === 0 && (
                    <React.Fragment>
                      <Form.Group className="mb-3">
                        <Form.Label>Upload File</Form.Label>

                        <div className="custom-file-upload-wrapper">
                          <label
                            htmlFor="file-upload"
                            className="custom-file-upload-btn"
                            style={{
                              "--dynamic-font-color":
                                whitelabel?.fontColor ?? "#fff",
                              "--dynamic-bg-color":
                                whitelabel?.backgroundColor ?? "#0E1A2B",
                              border: "none",
                            }}
                          >
                            Choose File
                          </label>
                          <input
                            key={fileInputKey}
                            id="file-upload"
                            type="file"
                            name="file"
                            accept=".csv,.xlsx"
                            style={{ display: "none" }}
                            onChange={async (event) => {
                              setFieldValue(
                                "file",
                                event.currentTarget.files[0]
                              );
                              await validateSimsForAction({
                                file: event.currentTarget.files[0],
                                action: "resume",
                              })
                                .then((response) => {
                                  notyf.open({
                                    type: "success",
                                    message: "Sim file validated successfully",
                                    duration: 5000,
                                    ripple: true,
                                    dismissible: false,
                                    position: {
                                      x: "center",
                                      y: "top",
                                    },
                                  });
                                  setFieldValue("sims", response);
                                })
                                .catch((error) => {
                                  notyf.open({
                                    type: "error",
                                    message: error,
                                    duration: 5000,
                                    ripple: true,
                                    dismissible: false,
                                    position: {
                                      x: "center",
                                      y: "top",
                                    },
                                  });
                                  setFieldValue("file", null);
                                  setFileInputKey(new Date());
                                });
                            }}
                            onBlur={() => setFieldTouched("file", true)}
                          />
                          {values.file && (
                            <div className="mt-2">
                              <small className="text-muted">
                                Selected: {values.file.name}
                              </small>
                            </div>
                          )}
                          {touched.file && errors.file && (
                            <div className="text-danger mt-1">
                              {errors.file}
                            </div>
                          )}
                        </div>
                      </Form.Group>
                    </React.Fragment>
                  )}
                  {currentStep === 1 && values.sims.length > 0 && (
                    <Table responsive hover>
                      <thead>
                        <tr>
                          <th>IMSI</th>
                          <th>ICCID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {values.sims.map((item, index) => {
                          return (
                            <tr key={index}>
                              <td>{item.imsi}</td>
                              <td>{item.iccid}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  )}
                  {currentStep === 2 && <Loader />}
                  {currentStep === 3 && status === "Success" && (
                    <div className="text-center">
                      <FaCheckCircle
                        size={120}
                        className="mt-3"
                        color="#4BBF73"
                      />
                      <h3 className="mt-3">Order Complete</h3>
                    </div>
                  )}
                  {currentStep === 3 && status === "Error" && (
                    <div className="text-center">
                      <FaExclamationCircle
                        size={120}
                        className="mt-3"
                        color="#FF9494"
                      />
                      <h3 className="mt-3">Order Error</h3>
                      <p>Please contact the administrator.</p>
                    </div>
                  )}
                  <Modal.Footer>
                    {currentStep === 1 && (
                      <React.Fragment>
                        <Button
                          variant="primary"
                          onClick={() => setOpenResumeModal(false)}
                          style={{
                            "--dynamic-bg-color":
                              whitelabel?.backgroundColor ?? "#0E1A2B",
                            "--dynamic-font-color":
                              whitelabel?.fontColor ?? "#ffffff",
                            border: "none",
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          onClick={nextStep}
                          style={{
                            "--dynamic-bg-color":
                              whitelabel?.backgroundColor ?? "#0E1A2B",
                            "--dynamic-font-color":
                              whitelabel?.fontColor ?? "#ffffff",
                            border: "none",
                          }}
                        >
                          Resume
                        </Button>
                      </React.Fragment>
                    )}
                    {currentStep === 3 && (
                      <Button
                        onClick={() => {
                          setCurrentStep(1);
                          setOpenResumeModal(false);
                          if (clearSelectedRows) {
                            clearSelectedRows();
                          }
                        }}
                        style={{
                          "--dynamic-bg-color":
                            whitelabel?.backgroundColor ?? "#0E1A2B",
                          "--dynamic-font-color":
                            whitelabel?.fontColor ?? "#ffffff",
                          border: "none",
                        }}
                      >
                        Close
                      </Button>
                    )}
                  </Modal.Footer>
                </Form>
              );
            }}
          </Formik>
        </Modal.Body>
      </Modal>
    );
  } else {
    return (
      <Offcanvas
        show={internalShow}
        placement="bottom"
        onHide={handleHide}
        className={`fullscreen-offcanvas ${fadeOut ? "fade-out" : ""}`}
      >
        <Offcanvas.Header closeButton className="fullscreen-offcanvas-header">
          <Offcanvas.Title>Resume SIMs</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="fullscreen-offcanvas-body">
          <Formik
            initialValues={initialValues}
            validationSchema={validation}
            onSubmit={async (values, { setValues, setStatus }) => {
              const data = {
                sims: values.sims.map((sim) => sim.imsi),
              };
              await dispatch(resumeSims(data.sims))
                .then(() => {
                  notyf.open({
                    type: "success",
                    message: "SIMs resumed successfully",
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                  setStatus("Success");
                })
                .catch((error) => {
                  notyf.open({
                    type: "danger",
                    message: error,
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                  setStatus("Error");
                })
                .finally(() => {
                  setCurrentStep(3);
                  setValues(initialValues);
                });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              status,
              validateForm,
              setFieldTouched,
            }) => {
              const nextStep = () => {
                validateForm().then((errors) => {
                  if (Object.keys(errors).length === 0) {
                    if (currentStep === 1) {
                      setCurrentStep(2);
                      handleSubmit();
                    }
                  } else {
                    Object.keys(values).forEach((field) => {
                      setFieldTouched(field);
                    });
                  }
                });
              };

              return (
                <Form onSubmit={handleSubmit}>
                  {currentStep === 1 && (
                    <Table responsive hover>
                      <thead>
                        <tr>
                          <th>IMSI</th>
                          <th>ICCID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {values.sims.map((item, index) => {
                          return (
                            <tr key={index}>
                              <td>{item.imsi}</td>
                              <td>{item.iccid}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  )}
                  {currentStep === 2 && <Loader />}
                  {currentStep === 3 && status === "Success" && (
                    <div className="text-center">
                      <FaCheckCircle
                        size={80}
                        className="mt-3"
                        color="#4BBF73"
                      />
                      <h3 className="mt-3">Order Complete</h3>
                    </div>
                  )}
                  {currentStep === 3 && status === "Error" && (
                    <div className="text-center">
                      <FaExclamationCircle
                        size={80}
                        className="mt-3"
                        color="#FF9494"
                      />
                      <h3 className="mt-3">Order Error</h3>
                      <p>Please contact the administrator.</p>
                    </div>
                  )}
                  <div className="d-flex flex-column align-items-center mt-3">
                    {currentStep === 1 && (
                      <React.Fragment>
                        <Button
                          variant="primary"
                          onClick={nextStep}
                          style={{
                            "--dynamic-bg-color":
                              whitelabel?.backgroundColor ?? "#0E1A2B",
                            "--dynamic-font-color":
                              whitelabel?.fontColor ?? "#ffffff",
                            border: "none",
                            padding: "10px",
                            width: "100%",
                          }}
                          className="mb-3"
                        >
                          Resume
                        </Button>
                        <Button
                          variant="primary"
                          onClick={handleHide}
                          style={{
                            "--dynamic-bg-color":
                              whitelabel?.backgroundColor ?? "#0E1A2B",
                            "--dynamic-font-color":
                              whitelabel?.fontColor ?? "#ffffff",
                            border: "none",
                            padding: "10px",
                            width: "100%",
                          }}
                        >
                          Cancel
                        </Button>
                      </React.Fragment>
                    )}
                    {currentStep === 3 && (
                      <Button
                        onClick={() => {
                          setCurrentStep(1);
                          handleHide();
                          if (clearSelectedRows) {
                            clearSelectedRows();
                          }
                        }}
                        style={{
                          "--dynamic-bg-color":
                            whitelabel?.backgroundColor ?? "#0E1A2B",
                          "--dynamic-font-color":
                            whitelabel?.fontColor ?? "#ffffff",
                          border: "none",
                          padding: "10px",
                          width: "100%",
                        }}
                      >
                        Close
                      </Button>
                    )}
                  </div>
                </Form>
              );
            }}
          </Formik>
        </Offcanvas.Body>
      </Offcanvas>
    );
  }
};

export default ResumeSimModal;
